/* import __COLOCATED_TEMPLATE__ from './track-admin-presence.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { registerDestructor } from '@ember/destroyable';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { timeout, type TaskGenerator } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import type RealtimeWorkflowParticipants from 'embercom/services/realtime-workflow-participants';
import { POLL_INTERVAL } from 'embercom/services/realtime-workflow-participants';
import type Session from 'embercom/services/session';
import type Nexus from 'embercom/services/nexus';
import { NexusStatusEvents } from 'embercom/services/nexus';
import ENV from 'embercom/config/environment';

interface Args {
  rulesetId: number;
}

interface Signature {
  Args: Args;
}

export default class WorkflowsEditorTrackAdminPresence extends Component<Signature> {
  @service declare realtimeWorkflowParticipants: RealtimeWorkflowParticipants;
  @service declare session: Session;
  @service declare nexus: Nexus;

  private lastTrackedId?: number;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    if (!this.nexus.isConnected && this.session.isTeammateLoaded) {
      this.nexus.start();
    }

    let handler = () => taskFor(this.updateWorkflowBeingViewed).perform();
    this.nexus.on(NexusStatusEvents.Reconnected, handler);

    registerDestructor(this, () => {
      this.nexus.off(NexusStatusEvents.Reconnected, handler);
    });
  }

  @task({ restartable: true })
  *updateWorkflowBeingViewed(): TaskGenerator<void> {
    while (true) {
      if (this.isDestroying || this.isDestroyed) {
        return;
      }
      if (document.visibilityState === 'visible' && this.session.isTeammateLoaded) {
        this.lastTrackedId = this.args.rulesetId;
        this.realtimeWorkflowParticipants.viewingWorkflow(
          this.session.teammate,
          this.args.rulesetId,
        );
      }
      if (ENV.environment === 'test') {
        break;
      } else {
        yield timeout(POLL_INTERVAL);
      }
    }
  }

  @action
  stopViewingWorkflow(): void {
    if (!this.lastTrackedId || !this.session.isTeammateLoaded) {
      return;
    }

    this.realtimeWorkflowParticipants.stopViewingWorkflow(
      this.session.teammate,
      this.lastTrackedId,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::Editor::TrackAdminPresence': typeof WorkflowsEditorTrackAdminPresence;
    'workflows/editor/track-admin-presence': typeof WorkflowsEditorTrackAdminPresence;
  }
}
